import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { errorReportsService, normalizeError, type ErrorDiagnostic } from '../api';
import ErrorReportDialog from '../components/ErrorReportDialog';

interface ErrorReportingContextValue {
  reportError: (error?: unknown) => void;
  rememberError: (error: unknown, componentStack?: string) => void;
}

const ErrorReportingContext = createContext<ErrorReportingContextValue | undefined>(undefined);

export function useErrorReporting(): ErrorReportingContextValue {
  const value = useContext(ErrorReportingContext);
  if (!value) throw new Error('useErrorReporting must be used within ErrorReportingProvider');
  return value;
}

export const ErrorReportingProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [diagnostic, setDiagnostic] = useState<ErrorDiagnostic>();
  const [open, setOpen] = useState(false);

  const rememberError = useCallback((error: unknown, componentStack?: string) => {
    setDiagnostic(normalizeError(error, componentStack));
  }, []);
  const reportError = useCallback((error?: unknown) => {
    if (error !== undefined) setDiagnostic(normalizeError(error));
    setOpen(true);
  }, []);

  useEffect(() => {
    const onError = (event: ErrorEvent) => rememberError(event.error ?? new Error(event.message));
    const onRejection = (event: PromiseRejectionEvent) => rememberError(event.reason);
    const onApiError = (event: Event) => rememberError((event as CustomEvent<unknown>).detail);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    window.addEventListener('app-api-error', onApiError);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      window.removeEventListener('app-api-error', onApiError);
    };
  }, [rememberError]);

  const value = useMemo(() => ({ reportError, rememberError }), [reportError, rememberError]);
  return (
    <ErrorReportingContext.Provider value={value}>
      {children}
      <ErrorReportDialog
        open={open}
        diagnostic={diagnostic}
        onClose={() => setOpen(false)}
        onSubmit={(request) => errorReportsService.create(request)}
      />
    </ErrorReportingContext.Provider>
  );
};